'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseLineCustomerService } from '@/app/components/icons/BaseLineCustomerService';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { reportEvent } from '@/app/utils';
import { ContactInfo } from './ContactInfo';

/**
 * 「我的订阅」页右下角浮动客服入口。
 * - 视觉规格来自 Figma 32643:6510：圆形、深色半透明、白边、阴影、内置 24px 客服图标。
 * - 通过 Portal 注入到 workspace 全局浮动容器 `#floating-buttons-container`，
 *   与「新手任务」按钮共用同一堆叠栈，避免位置重叠。
 * - 点击展开 Dialog，内容仅含联系方式（沿用 `ContactInfo`）。
 */
export const HelpFloatButton: FC = () => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setContainer(document.getElementById('floating-buttons-container'));
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      reportEvent('element_click', {
        from_page: 'my-subscription',
        element_name: 'customer_service_float_button',
      });
    }
  };

  const button = (
    <button
      type="button"
      aria-label={t('payments.mySubscription.needHelp')}
      onClick={() => handleOpenChange(true)}
      className="flex size-12 items-center justify-center rounded-full border border-[rgba(255,255,255,0.16)] bg-[rgba(30,32,35,0.64)] text-white shadow-[0_10px_20px_0_rgba(0,0,0,0.25)] backdrop-blur-[10px] transition-transform hover:scale-105 active:scale-95"
    >
      <BaseLineCustomerService className="size-6" />
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {container ? createPortal(button, container) : null}
      <DialogContent className="w-[420px] max-w-[90vw] sm:w-[320px]">
        <DialogHeader>
          <DialogTitle className="text-center text-base">{t('payments.mySubscription.needHelp')}</DialogTitle>
        </DialogHeader>
        <ContactInfo className="justify-center text-center" />
      </DialogContent>
    </Dialog>
  );
};
